import { useEffect, useState } from "react";
import { ArrowDownToLine, ArrowUpFromLine, Send, Shield } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import DepositModal from "@/components/wallet/DepositModal";
import WithdrawModal from "@/components/wallet/WithdrawModal";
import TransferModal from "@/components/wallet/TransferModal";
import WhitelistManager from "@/components/wallet/WhitelistManager";

const MODES = [
  { id: "deposit", label: "Deposit", icon: ArrowDownToLine, hint: "Get a deposit address for the selected asset and network." },
  { id: "withdraw", label: "Withdraw", icon: ArrowUpFromLine, hint: "Send funds to a whitelisted external address." },
  { id: "transfer", label: "Send", icon: Send, hint: "Instantly send funds to another PaisaCX user." },
];

const PENDING = ["confirming", "broadcasting", "pending_admin"];

function PendingConfirmations() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    const pull = () =>
      api
        .get("/api/wallet/movements", { params: { limit: 30, skip: 0 } })
        .then(({ data }) => alive && setRows((data.movements || []).filter((m) => PENDING.includes(m.status)).slice(0, 5)))
        .catch(() => alive && setRows((r) => r || []));
    pull();
    const id = setInterval(pull, 8000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  return (
    <div className="rounded-xl border border-border bg-surface/50 p-6">
      <p className="text-sm font-semibold">Pending confirmations</p>
      {rows === null ? (
        <div className="mt-4 space-y-3">
          <div className="skeleton h-6 w-full" />
          <div className="skeleton h-6 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">Nothing in flight. Deposits and withdrawals appear here until they settle.</p>
      ) : (
        <div className="mt-4 space-y-4">
          {rows.map((m) => {
            const need = Number(m.required_confirmations || 0);
            const have = Number(m.confirmations || 0);
            const pct = m.status === "pending_admin" ? 0.15 : need ? Math.min(1, have / need) : 0.5;
            return (
              <div key={m.id}>
                <div className="flex justify-between text-xs">
                  <span className="num">
                    {Number(m.amount).toLocaleString("en-US", { maximumFractionDigits: 8 })} {m.asset} {m.category === "withdraw" ? "withdrawal" : m.category}
                  </span>
                  <span className="num text-muted-foreground">
                    {m.status === "pending_admin" ? "Awaiting approval" : `${have}/${need} confirmations`}
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-2">
                  <div className="brand-gradient h-full transition-all" style={{ width: `${Math.round(pct * 100)}%` }} />
                </div>
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground">Funds credit automatically at the final confirmation. No action needed from you.</p>
        </div>
      )}
    </div>
  );
}

export default function ProfileTransfer() {
  const [mode, setMode] = useState("deposit");
  const [assets, setAssets] = useState([]);
  const [asset, setAsset] = useState("USDT");
  const [openModal, setOpenModal] = useState(null);

  useEffect(() => {
    api
      .get("/api/wallet/balances")
      .then((r) => setAssets((r.data.balances || []).filter((b) => !b.is_fiat)))
      .catch(() => setAssets([]));
  }, []);

  const current = MODES.find((m) => m.id === mode);
  const selected = assets.find((a) => a.asset === asset);

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Deposit and withdrawal</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Move crypto in and out of your PaisaCX wallet. Network fees and confirmations update live.
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
          <div className="rounded-xl border border-border bg-surface/50 p-6">
            <div className="flex gap-1 rounded-lg border border-border bg-surface-2 p-1">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMode(m.id)}
                  data-testid={`transfer-mode-${m.id}`}
                  className={`flex-1 rounded-md py-2 text-sm font-medium transition ${
                    mode === m.id ? "brand-gradient text-primary-foreground" : "text-muted-foreground"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <label className="mt-5 block text-xs text-muted-foreground" htmlFor="tr-asset">
              Asset
            </label>
            <select
              id="tr-asset"
              value={asset}
              onChange={(e) => setAsset(e.target.value)}
              data-testid="transfer-asset"
              className="mt-1.5 w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-primary"
            >
              {(assets.length ? assets : [{ asset: "USDT", name: "Tether" }]).map((a) => (
                <option key={a.asset} value={a.asset}>
                  {a.name} ({a.asset})
                </option>
              ))}
            </select>

            {selected && (
              <p className="mt-3 text-xs text-muted-foreground">
                Available: <span className="num text-foreground">{Number(selected.available || 0).toLocaleString("en-US", { maximumFractionDigits: selected.decimals ?? 8 })}</span>{" "}
                {selected.asset}
                {selected.networks?.length ? ` · Networks: ${selected.networks.join(", ")}` : ""}
              </p>
            )}

            <p className="mt-5 text-sm text-muted-foreground">{current.hint}</p>
            <button
              type="button"
              onClick={() => setOpenModal({ type: mode, asset })}
              data-testid="transfer-continue"
              className="brand-gradient mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold text-primary-foreground"
            >
              <current.icon size={15} /> {current.label} {asset}
            </button>
          </div>

          <div className="space-y-4">
            <PendingConfirmations />
            <div className="rounded-xl border border-border bg-surface/50 p-6">
              <p className="text-sm font-semibold">Address whitelist</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Withdrawals only go to approved addresses. Deposits from addresses that are not whitelisted are held until
                you approve them.
              </p>
              <button
                type="button"
                onClick={() => setOpenModal({ type: "whitelist" })}
                data-testid="transfer-whitelist"
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm transition hover:border-primary"
              >
                <Shield size={14} /> Manage whitelist
              </button>
            </div>
          </div>
        </div>
      </div>

      {openModal?.type === "deposit" && <DepositModal initialAsset={openModal.asset} onClose={() => setOpenModal(null)} />}
      {openModal?.type === "withdraw" && <WithdrawModal initialAsset={openModal.asset} onClose={() => setOpenModal(null)} />}
      {openModal?.type === "transfer" && <TransferModal initialAsset={openModal.asset} onClose={() => setOpenModal(null)} />}
      {openModal?.type === "whitelist" && <WhitelistManager onClose={() => setOpenModal(null)} />}
    </AppShell>
  );
}
